<template>
  <div class="flex justify-between border-gray-200">
    <h1 class="font-semibold text-3xl mb-1">Invoices</h1>
    <DownloadButton
      title="Download CSV"
      label="Download"
      :downloading="isDownloadingCsv"
      :disabled="invoices.length === 0"
      @click="downloadCsv"
    />
  </div>

  <div class="bg-white border border-gray-200 rounded-lg mt-4">
    <InvoicesControls
      @update:status="onStatusChange"
      @update:date-range="onDateRangeChange"
      @update:clear-filters="onClearFilters"
      @update:search-term="onSearchTermChange"
    />
    <DataTable
      :is-loading="isLoading"
      :columns="invoicesTableColumns"
      :data="invoices"
      :total-count="totalCount"
      :page-number="pageNumber"
      @update:page="onUpdatePage"
      @update:page-size="onUpdatePageSize"
    />
  </div>
</template>

<script setup lang="ts">
import DownloadButton from '@/components/common/DownloadButton.vue';
import { NetworkError } from '@/errors';
import { fetchInvoices, type InvoiceServiceGetManyRequest } from '@/services/invoices';
import { useGeneralStore } from '@/stores/general';
import { fetchAllPaginated, generateCsvBlobUrl } from '@/utils/export.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { deepEqual } from '@/utils/paging.utils';
import dayjs from 'dayjs';
import type { DateRange } from 'radix-vue';
import { onMounted, ref } from 'vue';
import type { ModelInvoice } from '@layup/api-sdk';
import { displayDate, toRandString } from '../../../../utils/format.utils';
import DataTable from '../../DataTable.vue';
import InvoicesControls from '../invoices-controls/InvoicesControls.vue';
import { invoicesTableColumns } from './InvoicesTableColumns';

const generalStore = useGeneralStore();

const isLoading = ref(true);
const invoiceParams = ref<InvoiceServiceGetManyRequest>({});
const previousParams = ref<InvoiceServiceGetManyRequest>({ ...invoiceParams.value });
const invoices = ref<Partial<ModelInvoice>[]>([]);

const totalCount = ref(1);
const pageNumber = ref(1);

onMounted(async () => onUpdateFilter(invoiceParams.value, true));

const onUpdateFilter = async (params: InvoiceServiceGetManyRequest, skipCheck: boolean = false) => {
  if (!skipCheck) {
    if (deepEqual(params, previousParams.value)) {
      return;
    }
    previousParams.value = { ...params };
  }

  invoices.value = [];
  isLoading.value = true;

  try {
    params.limit = generalStore.pageSize;
    params.skip = (pageNumber.value - 1) * generalStore.pageSize;
    const response = await fetchInvoices(params);
    invoices.value = response.invoices || [];
    totalCount.value = response.totalCount || 0;
    addMetric('invoices-table', { status: 'success', channel: 'merchant' });
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('invoices-table', { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric('invoices-table', { status: 'error', channel: 'merchant' });
    }
  } finally {
    isLoading.value = false;
  }
};

const onStatusChange = (value: string): void => {
  invoiceParams.value.status = value;
  onUpdateFilter(invoiceParams.value);
};

const onDateRangeChange = (value: DateRange): void => {
  invoiceParams.value.from = `${value.start?.year}-${value.start?.month}-${value.start?.day}`;
  invoiceParams.value.to = `${value.end?.year}-${value.end?.month}-${value.end?.day}`;
  onUpdateFilter(invoiceParams.value);
};

const onClearFilters = (): void => {
  pageNumber.value = 1;
  invoiceParams.value = { skip: 0 };
  onUpdateFilter(invoiceParams.value);
};

const onSearchTermChange = (value: string): void => {
  invoiceParams.value.reference = value.toUpperCase();
  onUpdateFilter(invoiceParams.value);
};

const onUpdatePage = (pageNo: number) => {
  pageNumber.value = pageNo;
  invoiceParams.value.skip = (pageNumber.value - 1) * generalStore.pageSize;
  onUpdateFilter(invoiceParams.value);
};

const onUpdatePageSize = (size: number) => {
  pageNumber.value = 1;
  generalStore.pageSize = size;
  invoiceParams.value.limit = size;
  onUpdateFilter(invoiceParams.value);
};

const isDownloadingCsv = ref(false);

const downloadCsv = async () => {
  isDownloadingCsv.value = true;
  try {
    const invoices = await fetchAllPaginated<ModelInvoice, InvoiceServiceGetManyRequest>(
      fetchInvoices,
      invoiceParams.value,
      'invoices',
    );

    if (invoices.length === 0) {
      console.warn('No data available for export');
      return;
    }
    const headerMap: Partial<Record<keyof ModelInvoice, string>> = {
      createdAt: 'Created Date',
      accountingInvoiceNumber: 'Invoice Number',
      totalAmount: 'Total Amount',
      reference: 'Reference',
      status: 'Status',
    };
    const data = invoices.map((invoice) => {
      return {
        ...invoice,
        createdAt: invoice.createdAt ? displayDate(invoice.createdAt) : '',
        totalAmount: toRandString(invoice.totalAmount ?? 0),
      };
    });

    const url = generateCsvBlobUrl(headerMap, data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `invoices-${dayjs().format('YYYY-MM-DD')}.csv`);
    link.click();
    URL.revokeObjectURL(url);
  } finally {
    isDownloadingCsv.value = false;
  }
};
</script>
